import { announceCartFeedback, getCartErrorMessage, getCartSuccessMessage } from '@theme/cart-feedback';

class SProductForm extends HTMLElement {
  #ac;
  #abortController;
  #activeSubmitter;
  #activeSubmitterAriaDisabled;
  #activeSubmitterHadOpacityClass;
  #activeSubmitterHadCursorWaitClass;

  connectedCallback() {
    this.#ac = new AbortController();
    const opts = { signal: this.#ac.signal };

    this.addEventListener('submit', (event) => {
      if (!this.#shouldHandleSubmit(event)) return;

      event.preventDefault();
      if (this.getAttribute('aria-busy') === 'true') return;

      this.#submit(event.target, event.submitter);
    }, opts);
  }

  disconnectedCallback() {
    this.#ac?.abort();
    this.#abortController?.abort();
    this.#setSubmitting(false);
  }

  #shouldHandleSubmit(event) {
    const form = event.target;
    if (!(form instanceof HTMLFormElement)) return false;

    // If Standard Actions are unavailable, leave the Shopify product form native.
    if (!window.Shopify?.actions?.updateCart) return false;

    if (!this.#variantInput(form)) return false;

    const submitter = event.submitter;
    if (submitter instanceof HTMLElement && !submitter.matches('button[name="add"], input[name="add"]')) {
      return false;
    }

    return true;
  }

  async #submit(form, submitter) {
    this.#abortController?.abort();
    this.#abortController = new AbortController();
    const { signal } = this.#abortController;

    const line = this.#lineFromFormData(new FormData(form));
    if (!line) {
      this.#abortController = null;
      return;
    }

    // Capture the fly-to-cart origin now: the quick-add modal closes right after submit.
    let flyOrigin = null;
    try {
      flyOrigin = this.#captureFlyOrigin(submitter);
    } catch {
      flyOrigin = null;
    }

    this.#setSubmitting(true, submitter);
    this.#showError('');

    // Start the fly-to-cart right away so the tap feels instant; the cart request runs alongside it.
    // Decoration must never turn a successful add into an error, so the flight swallows its own failures.
    const flight = this.#flyToCart(flyOrigin).catch(() => {});

    try {
      const added = await this.#addToCart(form, line, signal);
      if (signal.aborted) return;

      if (added.error) {
        this.#showError(added.error);
        return;
      }

      await flight;
      if (signal.aborted) return;
      this.#announceAdded();
    } catch (error) {
      if (signal.aborted) return;
      this.#showError(getCartErrorMessage(error) || error?.message || this.dataset.errorMessage || '');
    } finally {
      if (!signal.aborted) {
        this.#setSubmitting(false);
        if (this.#abortController?.signal === signal) this.#abortController = null;
      }
    }
  }

  // Adds the line through Standard Actions; a request failure without product errors retries through the form's own cart route.
  async #addToCart(form, line, signal) {
    try {
      const result = await window.Shopify.actions.updateCart(
        { lines: [line] },
        {
          event: {
            context: 'product',
            detail: this.#eventDetail(),
          },
          signal,
        }
      );
      this.#throwIfAborted(signal);
      const errorMessage = this.#resultErrorMessage(result);
      return errorMessage ? { error: errorMessage } : {};
    } catch (error) {
      if (signal.aborted) throw error;
      // Product-level problems (like stock) arrive as userErrors and are shown inline.
      if (error?.cause?.userErrors?.length || !(form instanceof HTMLFormElement)) {
        return { error: getCartErrorMessage(error) || this.dataset.errorMessage || '' };
      }
      await this.#addWithFormRoute(form, signal);
      return {};
    }
  }

  async #addWithFormRoute(form, signal) {
    // The form action is the Liquid-rendered cart add route; the AJAX contract needs `.js`.
    const action = form.getAttribute('action');
    if (!action) throw new Error(this.dataset.errorMessage || '');

    const response = await fetch(`${action}.js`, {
      method: 'POST',
      body: new FormData(form),
      headers: { Accept: 'application/json' },
      signal,
    });

    if (!response.ok) {
      let message = '';
      try {
        const data = await response.json();
        message = data?.description || data?.message || '';
      } catch {
        message = '';
      }
      throw new Error(message || this.dataset.errorMessage || '');
    }

    try {
      await customElements.whenDefined('s-cart');
      await customElements.get('s-cart')?.refreshMountedCarts?.({ signal });
    } catch {
      // The item is in the cart even if the drawer markup could not refresh.
    }
  }

  #announceAdded() {
    // The cart stays closed: badges refresh and assistive tech hears the confirmation.
    document.dispatchEvent(new CustomEvent('xisa:cart-added', { detail: { productId: this.dataset.productId || '' } }));
    announceCartFeedback(getCartSuccessMessage([{ quantity: 1 }])).catch(() => {});
  }

  #prefersReducedMotion() {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  #isInViewport(rect) {
    return rect.width > 0 && rect.height > 0 && rect.bottom > 0 && rect.right > 0
      && rect.top < window.innerHeight && rect.left < window.innerWidth;
  }

  #captureFlyOrigin(submitter) {
    if (this.#prefersReducedMotion()) return null;

    const scope = this.closest('dialog, [data-quick-add-source]');
    const candidates = scope
      ? [...scope.querySelectorAll('img')]
      : [...document.querySelectorAll('s-product-image-zoom img:not([data-zoom-image])')];
    const sourceImage = candidates.find((img) => img.currentSrc || img.src) || null;
    // A brand paw flies when the form provides one; otherwise the product photo does.
    const charm = this.dataset.flyImage || '';
    const src = charm || (sourceImage ? (sourceImage.currentSrc || sourceImage.src) : '');
    if (!src) return null;

    const visibleImage = candidates.find((img) => this.#isInViewport(img.getBoundingClientRect()));
    let rect = visibleImage?.getBoundingClientRect();
    if (!rect && submitter instanceof HTMLElement) rect = submitter.getBoundingClientRect();
    if (!rect || !this.#isInViewport(rect)) return null;

    return { src, rect, charm: Boolean(charm) };
  }

  #flyTarget() {
    // The floating gem cart is the destination whenever the page has it.
    const fab = document.querySelector('[data-cart-fab]');
    if (fab instanceof HTMLElement && fab.getBoundingClientRect().width > 0) return fab;
    const header = document.getElementById('header-cart-trigger');
    return header instanceof HTMLElement && this.#isInViewport(header.getBoundingClientRect()) ? header : null;
  }

  #topLayer(el) {
    // A manual popover renders above any open dialog, so the flight stays visible.
    el.setAttribute('popover', 'manual');
    // Reset the popover's UA box first so the caller's left/top win (the inset shorthand would erase them).
    el.style.cssText = 'position:fixed;inset:auto;margin:0;padding:0;border:0;background:transparent;overflow:visible;pointer-events:none;' + el.style.cssText;
    document.body.append(el);
    el.showPopover?.();
  }

  async #flyToCart(origin) {
    if (!origin || this.#prefersReducedMotion() || typeof Element.prototype.animate !== 'function') return;
    const target = this.#flyTarget();
    if (!target) return;

    const ease = 'cubic-bezier(0.16, 1, 0.3, 1)';
    const size = origin.charm ? 48 : Math.max(64, Math.min(150, origin.rect.width * 0.55, origin.rect.height * 0.55));
    const startX = origin.rect.left + origin.rect.width / 2 - size / 2;
    const startY = origin.rect.top + origin.rect.height / 2 - size / 2;
    const targetRect = target.getBoundingClientRect();
    const endX = targetRect.left + targetRect.width / 2 - size / 2;
    const endY = targetRect.top + targetRect.height / 2 - size / 2;
    const dx = endX - startX;
    const dy = endY - startY;
    const lift = Math.min(90, Math.abs(dx) * 0.15 + 40);

    const flyer = document.createElement('div');
    flyer.setAttribute('aria-hidden', 'true');
    flyer.style.cssText = `left:${startX}px;top:${startY}px;width:${size}px;height:${size}px;`;
    const img = document.createElement('img');
    img.src = origin.src;
    img.alt = '';
    img.style.cssText = origin.charm
      ? 'display:block;width:100%;height:100%;object-fit:contain;filter:drop-shadow(0 8px 10px rgb(176 0 95 / 0.3));'
      : 'display:block;width:100%;height:100%;object-fit:cover;border-radius:9999px;background:#fff;border:3px solid #f6c9dc;box-shadow:0 0 0 2px #fff, 0 10px 24px rgb(214 0 127 / 0.35);';
    flyer.append(img);
    this.#topLayer(flyer);

    // Pink dust trail: drop soft particles from the photo's current position while it flies.
    const trail = document.createElement('div');
    trail.setAttribute('aria-hidden', 'true');
    trail.style.cssText = 'left:0;top:0;width:0;height:0;';
    this.#topLayer(trail);
    const trailAnimations = [];
    const trailTimer = setInterval(() => {
      const box = flyer.getBoundingClientRect();
      if (!box.width) return;
      for (let i = 0; i < 3; i += 1) {
        trailAnimations.push(this.#dustParticle(trail, box.left + box.width / 2, box.top + box.height / 2, {
          spread: box.width * 0.45, distance: 14 + Math.random() * 16, fall: 30 + Math.random() * 20,
          duration: 1100 + Math.random() * 500, maxSize: 8,
        }));
      }
    }, 45);

    try {
      await flyer.animate([
        { transform: 'translate(0, 0) scale(0.96) rotate(0deg)', opacity: 0 },
        { transform: 'translate(0, -10px) scale(1.06) rotate(-12deg)', opacity: 1, offset: 0.14 },
        { transform: `translate(${dx * 0.5}px, ${dy * 0.5 - lift}px) scale(0.75) rotate(10deg)`, opacity: 1, offset: 0.55 },
        { transform: `translate(${dx}px, ${dy}px) scale(0.16) rotate(0deg)`, opacity: 0 },
      ], { duration: 1500, easing: 'cubic-bezier(0.4, 0, 0.3, 1)', fill: 'forwards' }).finished;
    } catch {
      // Interrupted animations still continue to the cart.
    } finally {
      clearInterval(trailTimer);
      flyer.remove();
      Promise.allSettled(trailAnimations).then(() => trail.remove());
    }

    // A soft plush "catch": the button squishes gently and settles, no flash.
    target.animate([
      { transform: 'scale(1, 1)' },
      { transform: 'scale(1.1, 0.9)', offset: 0.25 },
      { transform: 'scale(0.96, 1.05)', offset: 0.55 },
      { transform: 'scale(1.02, 0.99)', offset: 0.8 },
      { transform: 'scale(1, 1)' },
    ], { duration: 760, easing: 'ease-in-out', composite: 'add' });
    this.#sparkleBurst(endX + size / 2, endY + size / 2, ease);

    await new Promise((resolve) => setTimeout(resolve, 260));
  }

  #sparkleBurst(x, y, ease) {
    const burst = document.createElement('div');
    burst.setAttribute('aria-hidden', 'true');
    burst.style.cssText = `left:${x}px;top:${y}px;width:0;height:0;`;
    this.#topLayer(burst);

    // A faint pink glow that breathes out slowly.
    const ring = document.createElement('span');
    ring.style.cssText = 'position:absolute;left:-34px;top:-34px;width:68px;height:68px;border-radius:9999px;background:radial-gradient(circle, rgb(255 182 213 / 0.45) 0%, rgb(255 143 195 / 0.15) 50%, rgb(255 143 195 / 0) 72%);';
    burst.append(ring);
    const animations = [ring.animate([
      { transform: 'scale(0.6)', opacity: 0 },
      { transform: 'scale(1.2)', opacity: 1, offset: 0.35 },
      { transform: 'scale(1.7)', opacity: 0 },
    ], { duration: 1100, easing: 'ease-out', fill: 'forwards' }).finished];

    // Pink powder drifts up softly, like dust lifted off a plush.
    for (let i = 0; i < 18; i += 1) {
      animations.push(this.#dustParticle(burst, 0, -6, {
        spread: 26, distance: 8 + Math.random() * 20, fall: -(18 + Math.random() * 26),
        duration: 1200 + Math.random() * 600, maxSize: 7,
      }));
    }

    // Two or three little paw prints float up and fade.
    const heartCount = 2 + Math.round(Math.random());
    for (let i = 0; i < heartCount; i += 1) {
      animations.push(this.#floatingPaw(burst, (i - (heartCount - 1) / 2) * 14, i * 140));
    }

    Promise.allSettled(animations).then(() => burst.remove());
  }

  #floatingPaw(layer, offsetX, delay) {
    const heartSize = 10 + Math.random() * 5;
    const heart = document.createElement('span');
    heart.style.cssText = `position:absolute;left:${offsetX - heartSize / 2}px;top:${-heartSize}px;width:${heartSize}px;height:${heartSize}px;opacity:0;filter:drop-shadow(0 1px 2px rgb(176 0 95 / 0.35));`;
    heart.innerHTML = '<svg viewBox="0 0 24 24" width="100%" height="100%" aria-hidden="true"><g fill="#e0207f" stroke="#fff" stroke-width="1.2"><ellipse cx="5" cy="10" rx="2.6" ry="3.2"/><ellipse cx="9.3" cy="5.2" rx="2.7" ry="3.4"/><ellipse cx="14.7" cy="5.2" rx="2.7" ry="3.4"/><ellipse cx="19" cy="10" rx="2.6" ry="3.2"/><path d="M12 11.5c3.6 0 6.6 3.1 6.6 6.2 0 2.2-1.7 3.5-3.6 3.5-1.2 0-1.9-.5-3-.5s-1.8.5-3 .5c-1.9 0-3.6-1.3-3.6-3.5 0-3.1 3-6.2 6.6-6.2z"/></g></svg>';
    layer.append(heart);
    const drift = (Math.random() - 0.5) * 16;
    return heart.animate([
      { transform: 'translate(0, 0) scale(0.4)', opacity: 0 },
      { transform: `translate(${drift * 0.4}px, -14px) scale(1)`, opacity: 1, offset: 0.3 },
      { transform: `translate(${drift}px, -46px) scale(0.85)`, opacity: 0 },
    ], { duration: 1400, delay, easing: 'ease-out', fill: 'forwards' }).finished.finally(() => heart.remove());
  }

  #dustParticle(layer, x, y, { spread, distance, fall, duration, maxSize }) {
    const colors = ['#ff8fc3', '#f6c9dc', '#ffd6e8', '#d6007f', '#ffb3d4', '#ffffff', '#f0a8c8', '#e8c27a'];
    const color = colors[Math.floor(Math.random() * colors.length)];
    const dotSize = 2 + Math.random() * (maxSize - 2);
    const angle = Math.random() * Math.PI * 2;
    const ox = x + (Math.random() - 0.5) * spread;
    const oy = y + (Math.random() - 0.5) * spread;
    const tx = Math.cos(angle) * distance;
    const ty = Math.sin(angle) * distance;

    const dot = document.createElement('span');
    dot.style.cssText = `position:absolute;left:${ox - dotSize / 2}px;top:${oy - dotSize / 2}px;width:${dotSize}px;height:${dotSize}px;border-radius:9999px;background:${color};box-shadow:0 0 ${dotSize + 2}px ${color};filter:blur(${dotSize > 6 ? 1 : 0.4}px);`;
    layer.append(dot);

    return dot.animate([
      { transform: 'translate(0, 0) scale(0.5)', opacity: 0 },
      { transform: `translate(${tx * 0.55}px, ${ty * 0.55}px) scale(1)`, opacity: 0.95, offset: 0.2 },
      { transform: `translate(${tx}px, ${ty + fall}px) scale(0.35)`, opacity: 0 },
    ], { duration, easing: 'cubic-bezier(0.16, 1, 0.3, 1)', fill: 'forwards' }).finished.finally(() => dot.remove());
  }

  #variantInput(form) {
    const input = form.elements.namedItem('id');
    return input instanceof HTMLInputElement ? input : null;
  }

  #lineFromFormData(formData) {
    const variantId = String(formData.get('id') || '').trim();
    if (!variantId) return null;

    const quantityValue = Number.parseInt(String(formData.get('quantity') || '1'), 10);
    const quantity = Number.isFinite(quantityValue) && quantityValue > 0 ? quantityValue : 1;
    const line = {
      merchandiseId: variantId,
      quantity,
    };

    const sellingPlanId = String(formData.get('selling_plan') || '').trim();
    if (sellingPlanId) line.sellingPlanId = sellingPlanId;

    const attributes = this.#lineAttributes(formData);
    if (attributes.length) line.attributes = attributes;

    return line;
  }

  #lineAttributes(formData) {
    const attributes = [];

    for (const [name, value] of formData) {
      const match = String(name).match(/^properties\[(.+)]$/);
      if (!match) continue;
      if (typeof File !== 'undefined' && value instanceof File) continue;

      const attributeValue = String(value).trim();
      if (!attributeValue) continue;

      attributes.push({ key: match[1], value: attributeValue });
    }

    return attributes;
  }

  #eventDetail() {
    const detail = { source: 's-product-form' };
    if (this.dataset.productId) detail.productId = this.dataset.productId;
    return detail;
  }

  #resultErrorMessage(result) {
    return result?.userErrors?.[0]?.message || '';
  }

  #showError(message) {
    const error = this.querySelector('[data-add-to-cart-error]');
    if (!error) return;

    error.textContent = message || '';
    error.hidden = !message;
  }

  #setSubmitting(submitting, submitter = this.#activeSubmitter) {
    if (submitting) {
      this.setAttribute('aria-busy', 'true');
      if (submitter instanceof HTMLElement) {
        this.#activeSubmitter = submitter;
        this.#activeSubmitterAriaDisabled = submitter.getAttribute('aria-disabled');
        this.#activeSubmitterHadOpacityClass = submitter.classList.contains('opacity-50');
        this.#activeSubmitterHadCursorWaitClass = submitter.classList.contains('cursor-wait');
        submitter.setAttribute('aria-disabled', 'true');
        submitter.classList.add('opacity-50', 'cursor-wait');
      }
      return;
    }

    this.removeAttribute('aria-busy');
    if (this.#activeSubmitter instanceof HTMLElement) {
      if (this.#activeSubmitterAriaDisabled == null) {
        this.#activeSubmitter.removeAttribute('aria-disabled');
      } else {
        this.#activeSubmitter.setAttribute('aria-disabled', this.#activeSubmitterAriaDisabled);
      }
      if (!this.#activeSubmitterHadOpacityClass) this.#activeSubmitter.classList.remove('opacity-50');
      if (!this.#activeSubmitterHadCursorWaitClass) this.#activeSubmitter.classList.remove('cursor-wait');
    }
    this.#activeSubmitter = null;
    this.#activeSubmitterAriaDisabled = null;
    this.#activeSubmitterHadOpacityClass = null;
    this.#activeSubmitterHadCursorWaitClass = null;
  }

  #throwIfAborted(signal) {
    if (!signal.aborted) return;

    if (typeof signal.throwIfAborted === 'function') {
      signal.throwIfAborted();
    }

    throw new DOMException('Aborted', 'AbortError');
  }
}

if (!customElements.get('s-product-form')) {
  customElements.define('s-product-form', SProductForm);
}
